/**
 * Auth HTTP Clients for Tankef
 *
 * Separate clients for auth operations to avoid circular dependencies
 * and different content-type requirements:
 * - authClient: JSON for password reset endpoints
 * - oauthClient: form-urlencoded for OAuth token endpoint
 */

import { config, logger } from '@tankef/env';
import axios from 'axios';

/**
 * Client for JSON-based auth endpoints
 * Used for: password reset, user info, etc.
 */
export const authClient = axios.create({
  baseURL: config.onboardingApiUrl, // Usar onboardingApiUrl que apunta al servicio correcto
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000,
});

/**
 * Client for OAuth 2.0 token endpoint
 * Uses application/x-www-form-urlencoded as per OAuth spec
 */
export const oauthClient = axios.create({
  baseURL: config.onboardingApiUrl,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    Accept: 'application/json',
  },
  timeout: 30000,
});

interface RetryableConfig {
  _retryCount?: number;
  url?: string;
  headers?: Record<string, string>;
}

const AUTH_MAX_RETRIES = 3;

/** 429 backoff for auth/oauth clients — token refresh endpoint MUST not fail silently */
async function handleAuthClientError(error: unknown): Promise<never> {
  if (axios.isAxiosError(error)) {
    const cfg = error.config as RetryableConfig & { url?: string };
    logger.error('[Auth] Request failed:', {
      url: cfg?.url,
      status: error.response?.status,
      message: error.response?.data?.error || error.message,
    });

    if (error.response?.status === 429 && cfg) {
      const retryCount = cfg._retryCount ?? 0;
      if (retryCount < AUTH_MAX_RETRIES) {
        (cfg as RetryableConfig)._retryCount = retryCount + 1;
        const retryAfter = error.response.headers['retry-after'];
        const delaySec = retryAfter ? parseInt(retryAfter, 10) : Math.pow(2, retryCount);
        const delayMs = (isNaN(delaySec) ? Math.pow(2, retryCount) : delaySec) * 1000;
        logger.warn(
          `[Auth] 429 on auth endpoint — retrying in ${delayMs / 1000}s (${retryCount + 1}/${AUTH_MAX_RETRIES})`
        );
        await new Promise(resolve => setTimeout(resolve, delayMs));
        return (error.config?.baseURL === oauthClient.defaults.baseURL ? oauthClient : authClient)(
          error.config!
        ) as Promise<never>;
      }
      logger.error('[Auth] 429 max retries reached on auth endpoint');
    }
  }
  return Promise.reject(error);
}

authClient.interceptors.response.use(response => response, handleAuthClientError);
oauthClient.interceptors.response.use(response => response, handleAuthClientError);
